import React, { useEffect, useRef, useCallback, useId } from "react";
import { CloseIcon } from "./Icons";

/**
 * Modal component with consistent behavior across the app:
 * - Click outside to close (only on mousedown + mouseup both on overlay)
 * - Escape key to close
 * - Enter key to submit (optional)
 * - Focus trap within modal
 */
function Modal({
  isOpen,
  onClose,
  onSubmit,
  title,
  children,
  footer,
  size = "default", // "small", "default", "large", "full"
  showCloseButton = true,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  submitOnEnter = true,
}) {
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const mouseDownTarget = useRef(null);
  const titleId = useId();

  // Handle escape/enter/tab keys
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Ignore key events when another modal is stacked on top of this one
      const overlays = document.querySelectorAll(".modal-overlay");
      if (
        overlays.length > 0 &&
        overlays[overlays.length - 1] !== overlayRef.current
      ) {
        return;
      }

      if (e.key === "Escape" && closeOnEscape) {
        e.preventDefault();
        onClose();
      } else if (e.key === "Enter" && submitOnEnter && onSubmit) {
        // Don't hijack Enter from buttons/selects/links - let them activate
        if (e.target.closest && e.target.closest("button, select, a")) {
          return;
        }
        // Only submit on Enter if not in a textarea or if Ctrl/Cmd+Enter
        const isTextarea = e.target.tagName === "TEXTAREA";
        const isModified = e.ctrlKey || e.metaKey;

        if (!isTextarea || isModified) {
          e.preventDefault();
          onSubmit();
        }
      } else if (e.key === "Tab" && modalRef.current) {
        // Focus trap: keep Tab cycling within the modal
        const focusable = modalRef.current.querySelectorAll(
          'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const inside = modalRef.current.contains(document.activeElement);

        if (e.shiftKey) {
          if (!inside || document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else if (!inside || document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeOnEscape, submitOnEnter, onClose, onSubmit]);

  // Focus modal on open
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Focus the first focusable element or the modal itself
      const focusable = modalRef.current.querySelector(
        'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) {
        focusable.focus();
      } else {
        modalRef.current.focus();
      }
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      // Only restore scroll if no other modal is still open
      if (!document.querySelector(".modal-overlay")) {
        document.body.style.overflow = "";
      }
    };
  }, [isOpen]);

  // Track mousedown target to prevent close on drag-out
  const handleMouseDown = useCallback((e) => {
    mouseDownTarget.current = e.target;
  }, []);

  const handleMouseUp = useCallback(
    (e) => {
      // Only close if both mousedown and mouseup were on the overlay
      if (
        closeOnOverlayClick &&
        mouseDownTarget.current === e.currentTarget &&
        e.target === e.currentTarget
      ) {
        onClose();
      }
      mouseDownTarget.current = null;
    },
    [closeOnOverlayClick, onClose]
  );

  if (!isOpen) return null;

  const sizeClass = {
    small: "modal-small",
    default: "",
    large: "modal-large",
    full: "modal-full",
  }[size];

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div
        ref={modalRef}
        className={`modal ${sizeClass}`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && <h2 id={titleId}>{title}</h2>}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="btn btn-ghost btn-icon"
                type="button"
              >
                <CloseIcon size={20} />
              </button>
            )}
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Confirm modal for simple yes/no dialogs
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmVariant = "primary", // "primary", "danger", "success"
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onConfirm}
      title={title}
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary">
            {cancelText}
          </button>
          <button onClick={onConfirm} className={`btn btn-${confirmVariant}`}>
            {confirmText}
          </button>
        </>
      }
    >
      {typeof message === "string" ? <p>{message}</p> : message}
    </Modal>
  );
}

export default Modal;
